import React, { useState, useMemo } from 'react';
// import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import { Autocomplete, GetTagProps, RenderInputParams } from '@material-ui/lab';
import { TextField, CircularProgress, Chip } from '@material-ui/core';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import gql from 'graphql-tag';
import { IAutoCompleteItem } from '../../typings/autoComplete';
import { useQuery } from 'react-apollo';

// const useStyles = makeStyles((theme: Theme) =>
//     createStyles({
//         removedAvatar: {
//             maxWidth: '15px',
//             maxHeight: '15px',
//         },
//     }),
// );

interface MatterCategorySelectorProps {
    disabled?: boolean;
    required?: boolean;
    // tslint:disable-next-line: no-any
    value?: MatterCategoryAutoCompleteItem | MatterCategoryAutoCompleteItem[] | any; // value only available in single select
    error?: boolean;
    onSelection?: (value?: MatterCategoryAutoCompleteItem | MatterCategoryAutoCompleteItem[], name?: string) => void;
    className?: string;
    helperText?: string;
    name?: string;
    label?: string;
    style?: React.CSSProperties;
    disablePortal?: boolean;
    variant?: 'standard' | 'filled' | 'outlined';
    size?: 'small' | 'medium';
    onBlur?: React.FocusEventHandler<HTMLDivElement>;
    multiple?: boolean;
    isApplyMatterAccess?: boolean;
    disableCloseOnSelect?: boolean;
}

export interface MatterCategoryAutoCompleteItem extends IAutoCompleteItem, MatterCategory {}

interface MatterCategorySelectorState {
    data: MatterCategoryAutoCompleteItem[];
}

export const MatterCategorySelector: React.FC<MatterCategorySelectorProps> = (props) => {
    // const classes = useStyles();

    const [state, setState] = useState<MatterCategorySelectorState>({
        data: [],
    });

    const matterCategoryQuery = useQuery<MatterCategoryData, MatterCategoryParams>(MatterCategoryQuery, {
        variables: {
            isApplyMatterAccess: props.isApplyMatterAccess
        },
        fetchPolicy: 'cache-first'
    });

    useMemo(() => {
        if (
            !matterCategoryQuery.loading &&
            !matterCategoryQuery.error &&
            matterCategoryQuery.data &&
            matterCategoryQuery.data.lookup &&
            matterCategoryQuery.data.lookup.matterCategory
        ) {
            setState((prevState) => {
                const { matterCategory } = matterCategoryQuery.data!.lookup;

                const items: MatterCategoryAutoCompleteItem[] = matterCategory.map((mCategory: MatterCategory) => {
                    return {
                        ...mCategory,
                        label: mCategory.description,
                        value: mCategory.description,
                    };
                }).sort((a, b) => {
                    if (a.label < b.label) {
                        return -1;
                    } else if (a.label > b.label) {
                        return 1;
                    } else {
                        return 0;
                    }
                });

                return {
                    ...prevState,
                    data: items,
                };
            });
        }
    // tslint:disable-next-line: align
    }, [matterCategoryQuery.loading, matterCategoryQuery.data, matterCategoryQuery.error]);

    // tslint:disable-next-line: no-any
    const handleChange = (event: React.ChangeEvent<{}>, value: any) => {
        if (props.onSelection) {
            props.onSelection(value, props.name);
        }
    };

    const getInputProps = (params: RenderInputParams, isLoading?: boolean) => {
        return {
            ...params.InputProps,
            endAdornment: (
                <React.Fragment>
                    {isLoading ? <CircularProgress color="primary" size={20} /> : null}
                    {params.InputProps.endAdornment}
                </React.Fragment>
            ),
        };
    };

    const onRenderTags = (value: MatterCategoryAutoCompleteItem[], getTagProps: GetTagProps): React.ReactNode => {
        let renderTagsValue = value.map((option: MatterCategoryAutoCompleteItem, index: number) => {
            let label = option.label;
            if (option.label.length > 20) {
                label = `${option.label.substring(0, 20)}...`;
            }

            return (
                <Chip
                    {...getTagProps({ index })}
                    key={index}
                    label={label}
                    // variant="outlined"
                    size="small"
                />
            );
        });

        return renderTagsValue;
    };

    if (props.multiple) {
        return (
            <Autocomplete
                style={props.style}
                className={props.className}
                value={props.value}
                loading={matterCategoryQuery.loading}
                disabled={props.disabled}
                onChange={handleChange}
                options={state.data}
                disablePortal={props.disablePortal} // to Stop the dropdown select from closing the form
                // tslint:disable-next-line: jsx-no-lambda
                getOptionLabel={(option: MatterCategoryAutoCompleteItem) => option.label}
                // tslint:disable-next-line: jsx-no-lambda
                onBlur={props.onBlur}
                multiple={true}
                renderTags={onRenderTags}
                disableCloseOnSelect={props.disableCloseOnSelect}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label={props.label}
                        variant={props.variant}
                        fullWidth={true}
                        margin="none"
                        required={props.required}
                        InputProps={getInputProps(params, matterCategoryQuery.loading)}
                        error={props.error}
                        size={props.size}
                        helperText={props.helperText}
                    />
                )}
                // tslint:disable-next-line: jsx-no-lambda
                renderOption={(option, { inputValue }) => {
                    const matches = match(option.label, inputValue);
                    const parts = parse(option.label, matches);

                    return (
                        <div>
                            {parts.map((part, index) => (
                                <span key={index} style={{ fontWeight: part.highlight ? 700 : 400 }}>
                                    {part.text}
                                </span>
                            ))}
                        </div>
                    );
                }}
            />
        );
    }

    return (
        <Autocomplete
            style={props.style}
            className={props.className}
            value={props.value}
            loading={matterCategoryQuery.loading}
            disabled={props.disabled}
            onChange={handleChange}
            options={state.data}
            disablePortal={props.disablePortal} // to Stop the dropdown select from closing the form
            // tslint:disable-next-line: jsx-no-lambda
            getOptionLabel={(option: MatterCategoryAutoCompleteItem) => option.label}
            // tslint:disable-next-line: jsx-no-lambda
            onBlur={props.onBlur}
            disableCloseOnSelect={props.disableCloseOnSelect}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={props.label}
                    variant={props.variant}
                    fullWidth={true}
                    margin="none"
                    required={props.required}
                    InputProps={getInputProps(params, matterCategoryQuery.loading)}
                    error={props.error}
                    size={props.size}
                    helperText={props.helperText}
                />
            )}
            // tslint:disable-next-line: jsx-no-lambda
            renderOption={(option, { inputValue }) => {
                const matches = match(option.label, inputValue);
                const parts = parse(option.label, matches);

                return (
                    <div>
                        {parts.map((part, index) => (
                            <span key={index} style={{ fontWeight: part.highlight ? 700 : 400 }}>
                                {part.text}
                            </span>
                        ))}
                    </div>
                );
            }}
        />
    );
};

export interface MatterCategoryParams {
    isApplyMatterAccess?: boolean;
}

export interface MatterCategoryData {
    lookup: Lookup;
}

export interface Lookup {
    matterCategory: MatterCategory[];
}

export interface MatterCategory {
    id: number;
    categoryId: string;
    description: string;
}

const MatterCategoryQuery = gql`
    query MatterCategory($isApplyMatterAccess: Boolean) {
        lookup {
            matterCategory(isApplyMatterAccess: $isApplyMatterAccess) {
                id
                categoryId
                description
            }
        }
    }
`;
